import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import authService from './services/authService';
import './html5up-theme.css';
import './ResourcesPage.css';

const ResourcesPage = () => {
    const [proteinSummaries, setProteinSummaries] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UniProt URL mappings for each protein
    const getUniProtUrl = (proteinName) => {
        // Extract simple protein name from full name
        const simpleName = proteinName.split('.')[0].trim();
        
        const uniprotMappings = {
            'PB2': 'https://uniprot.org/uniprotkb/P03428',
            'PB1': 'https://www.uniprot.org/uniprotkb/Q82571',
            'PB1-F2': 'https://www.uniprot.org/uniprotkb/A0A7D0TRI0',
            'PA': 'https://uniprot.org/uniprotkb/P03433',
            'NP': 'https://uniprot.org/uniprotkb/P03466',
            'NA': 'https://uniprot.org/uniprotkb/P03468',
            'M1': 'https://uniprot.org/uniprotkb/P03485',
            'M2': 'https://www.uniprot.org/uniprotkb/O70632/',
            'NS1': 'https://uniprot.org/uniprotkb/P03496',
            'NEP': 'https://uniprot.org/uniprotkb/P03508'
        };
        
        return uniprotMappings[simpleName] || null;
    };

    // Fetch protein summaries when component mounts
    useEffect(() => {
        fetchProteinSummaries();
    }, []);

    const fetchProteinSummaries = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const headers = {
                'Content-Type': 'application/json',
            };
            
            // Add authentication token if available
            const token = authService.getToken();
            if (token) {
                headers['Authorization'] = `Token ${token}`;
            }
            
            const response = await fetch('/api/protein-summaries/', {
                method: 'GET',
                headers: headers,
                credentials: 'include',
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to fetch protein summaries: ${response.status} - ${errorText}`);
            }
            
            const data = await response.json();
            setProteinSummaries(data.protein_summaries || {});
        } catch (err) {
            console.error('Error fetching protein summaries:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="resources-page">
            {/* VILLA Research Platform Header */}
            <header id="header">
                <h1>
                    <Link to="/" className="logo-link">
                        <img src={`${process.env.PUBLIC_URL}/compass_logo.png?v=3`} alt="VILLA Logo" className="navbar-logo" />
                        VILLA Research Platform
                    </Link>
                </h1>
                <nav>
                    <Link 
                        to="/select"
                        className="back-to-select-btn"
                        title="Back to Evaluation Tasks"
                    >
                        ← Back to Tasks
                    </Link>
                </nav>
            </header>
            
            <div className="container">
                {/* Page Header */}
                <div className="page-header">
                    <h1>Resources & Help</h1>
                </div>

                {/* Resources Content */}
                <div className="resources-content">
                    
                    {/* Background Section */}
                    <section className="resource-section">
                        <h2>Background</h2>
                        <div className="section-content">
                            <div className="content-text">
                                <p>
                                    To assess the ability of VILLA to perform scientific information extraction (SIE), we devised a test case model and Ground Truth dataset for a RAG encoder protocol. Our approach focused on the following biological question: <strong>What are the mutations in influenza A virus genomes that contribute to zoonosis and emergence threats in mammalian hosts?</strong>
                                </p>
                                <p>
                                    A rich primary literature of observed natural mutations, <em>in vitro</em> experimental work, and <em>in vivo</em> animal model studies, have identified many influenza genomic variants that modulate phenotypes (i.e. cell kinetics, virulence and transmission in an1imal models). We used a manually curated database of such mutations and their sourcing in the literature as shown in the figure.
                                </p>
                                <img 
                                    src={`${process.env.PUBLIC_URL}/dataset.png`} 
                                    alt="Dataset overview" 
                                    className="dataset-image"
                                />
                                <p>
                                    Preliminary results have assessed the <em>quantitative</em> performance of VILLA against this Ground Truth dataset (e.g. precision and recall of 'mutation' data points). We are looking to establish a <em>qualitative</em> measure of output performance.
                                </p>
                                <p>
                                    Our primary question is:<strong> Do LLM-based extractions of influenza mutational data aid researchers in synthesizing the literature and provide insights?</strong> Ultimately, you are being asked to score each reasoning on its own merits along the rubric. There is <em>no need</em> for consideration of head-to-head ('which model is best') or evaluating whether a model has 'solved' the much larger motivating biological question of influenza zoonosis.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Evaluation Task Section */}
                    <section className="resource-section">
                        <h2>Evaluation Task Overview</h2>
                        <div className="section-content">
                            <div className="content-text">
                                <p>
                                    Your task is to qualitatively evaluate the “Reasoning” for 4 different LLM model outputs, focusing on their biological significance and logic. All models have been prompted to return reported zoonotic substitution mutations in the specified viral protein. You will be shown models in a random order within each protein and asked to rank each response across 5 categories; 2 on language/syntax (Clarity, Conciseness), and 3 on biological relevance (Context, Correctness, Contribution). Each evaluation is on a 1 to 5 scale, with 1 being the lowest score, and 5 being the highest score. A general guide for each numeric response will appear when you roll your cursor over the score. During scoring, feel free to refer to the Ground Truth primer for each protein, which will be available.
                                </p>

                                <h3>Evaluation Criteria</h3>
                                <ul className="criteria-list">
                                    <li><strong>Clarity:</strong> How clearly are ideas and arguments are expressed?</li>
                                    <li><strong>Conciseness:</strong> Is language efficient and purposeful to convey all relevant information?</li>
                                    <li><strong>Correctness:</strong> Is the output verifiable against the Ground Truth dataset?</li>
                                    <li><strong>Citations/Context:</strong> Are context and citations relevant, authentic, and complete?</li>
                                    <li><strong>Contribution:</strong> Does the output engender significant or novel insight into the understanding of host-virus interactions? </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Protein Information Section */}
                    <section className="resource-section">
                        <h2>Protein Information</h2>
                        <div className="section-content">
                            <p>
                                Understanding influenza virus proteins is crucial for evaluating LLM outputs about zoonotic mutations. 
                                Below are detailed descriptions of each protein and their roles in viral function and host adaptation.
                            </p>
                            
                            {loading && (
                                <div className="protein-loading">
                                    <p>Loading protein information...</p>
                                </div>
                            )}
                            
                            {error && (
                                <div className="protein-error">
                                    <p>Error loading protein information: {error}</p>
                                    <button onClick={fetchProteinSummaries} className="retry-btn">
                                        Retry
                                    </button>
                                </div>
                            )}
                            
                            {!loading && !error && Object.keys(proteinSummaries).length > 0 && (
                                <div className="protein-summaries">
                                    {Object.entries(proteinSummaries)
                                        .sort(([a], [b]) => {
                                            // Custom protein order: PB2, PB1, PB1-F2, PA, NP, NA, M1, M2, NS1, NEP
                                            const proteinOrder = ['PB2', 'PB1', 'PB1-F2', 'PA', 'NP', 'NA', 'M1', 'M2', 'NS1', 'NEP'];
                                            
                                            // Extract simple protein names from full names (e.g., "PB2. 759aa." -> "PB2")
                                            const extractSimpleName = (fullName) => {
                                                return fullName.split('.')[0].trim();
                                            };
                                            
                                            const simpleNameA = extractSimpleName(a);
                                            const simpleNameB = extractSimpleName(b);
                                            
                                            const indexA = proteinOrder.indexOf(simpleNameA);
                                            const indexB = proteinOrder.indexOf(simpleNameB);
                                            
                                            // If both proteins are in the custom order, sort by index
                                            if (indexA !== -1 && indexB !== -1) {
                                                return indexA - indexB;
                                            }
                                            // If only one protein is in the custom order, it comes first
                                            if (indexA !== -1) return -1;
                                            if (indexB !== -1) return 1;
                                            // If neither protein is in the custom order, sort alphabetically
                                            return a.localeCompare(b);
                                        })
                                        .map(([proteinName, summary]) => {
                                            const uniprotUrl = getUniProtUrl(proteinName);
                                            
                                            return (
                                                <div key={proteinName} className="protein-summary">
                                                    <h3 className="protein-name">
                                                        {uniprotUrl ? (
                                                            <a 
                                                                href={uniprotUrl} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="protein-link"
                                                            >
                                                                {proteinName}
                                                            </a>
                                                        ) : (
                                                            proteinName
                                                        )}
                                                    </h3>
                                                    <p className="protein-description">{summary}</p>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            )}
                            
                            {!loading && !error && Object.keys(proteinSummaries).length === 0 && (
                                <div className="protein-empty">
                                    <p>No protein information available at this time.</p>
                                </div>
                            )}
                        </div>
                    </section>

                </div>
            </div>
        </div>
    );
};

export default ResourcesPage;